/**
 * Sandboxes v2 detail-page data: the sandbox, its action runs, and the run /
 * destroy mutations. Shapes are the API's (`apps/api/src/sandboxes-v2/routes.ts`).
 *
 * @module features/sandboxes/hooks/useSandboxV2Detail
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';
import { getJson, type SandboxV2SandboxStatus } from './useSandboxesV2';
import type { SandboxV2Operation } from '../../../hooks/useApplicationSandbox';

export type { SandboxV2Operation };

export type SandboxV2InputProp = {
  type: 'string' | 'number' | 'integer' | 'boolean' | 'persona';
  description?: string;
  required?: boolean;
  default?: string | number | boolean;
  enum?: Array<string | number>;
  sensitive?: boolean;
};

export type SandboxV2DeclaredAction = {
  slug: string;
  description: string;
  input: Record<string, SandboxV2InputProp>;
  output: Record<string, { type: string; description?: string }>;
  timeout: number;
};

export type SandboxV2Persona = { name: string; email: string; password: string };

export type SandboxV2Detail = {
  id: string;
  applicationId: string;
  applicationName: string | null;
  environment: string;
  version: string;
  status: SandboxV2SandboxStatus;
  error: string | null;
  portUrls: Record<string, string>;
  personas: SandboxV2Persona[];
  seedOutputs: Record<string, Record<string, unknown>>;
  leaseExpiresAt: string | null;
  expired: boolean;
  readyMs: number | null;
  phases: { snapshotMs: number; restoreMs: number; prepareMs: number } | null;
  snapshot: { builtAt: string; buildPhases: { buildMs: number } | null } | null;
  actions: SandboxV2DeclaredAction[];
  operations: SandboxV2Operation[];
};

export type SandboxV2RunStatus = 'running' | 'succeeded' | 'failed';

export type SandboxV2RunListItem = {
  id: string;
  action: string;
  seedStepId: string | null;
  input: Record<string, unknown>;
  status: SandboxV2RunStatus;
  exitCode: number | null;
  error: string | null;
  durationMs: number | null;
  createdAt: string;
  completedAt: string | null;
};

export type SandboxV2Run = SandboxV2RunListItem & {
  sandboxId: string;
  output: Record<string, unknown> | null;
  stdoutTail: string | null;
  stderrTail: string | null;
};

const DETAIL_KEY = (sandboxId: string) => ['sandbox-v2-detail', sandboxId] as const;
const RUNS_KEY = (sandboxId: string) => ['sandbox-v2-runs', sandboxId] as const;

/** The sandbox, polled while it is still claiming. */
export function useSandboxV2Detail(sandboxId: string, enabled: boolean) {
  return useQuery({
    queryKey: DETAIL_KEY(sandboxId),
    queryFn: async () =>
      (await getJson<{ data: SandboxV2Detail }>(`/org/sandboxes-v2/sandboxes/${sandboxId}`, 'Failed to load the sandbox')).data,
    enabled,
    refetchInterval: (query) => (query.state.data?.status === 'claiming' ? 3000 : false),
  });
}

export function useSandboxV2RunHistory(sandboxId: string, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: RUNS_KEY(sandboxId),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: '10' });
      if (pageParam) params.set('cursor', pageParam);
      return getJson<{ data: SandboxV2RunListItem[]; nextCursor: string | null }>(
        `/org/sandboxes-v2/sandboxes/${sandboxId}/runs?${params}`,
        'Failed to load run history'
      );
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
    refetchInterval: (query) =>
      query.state.data?.pages.some((page) => page.data.some((run) => run.status === 'running')) ? 3000 : false,
  });
}

/** One run with its output and logs; polled until it leaves `running`. */
export function useSandboxV2Run(runId: string | null) {
  return useQuery({
    queryKey: ['sandbox-v2-run', runId],
    queryFn: async () => (await getJson<{ data: SandboxV2Run }>(`/org/sandboxes-v2/runs/${runId}`, 'Failed to load the run')).data,
    enabled: runId !== null,
    refetchInterval: (query) => (query.state.data?.status === 'running' ? 1500 : false),
  });
}

export function useRunSandboxV2Action(sandboxId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { action: string; input: Record<string, unknown> }) => {
      const res = await fetch(`${API_URL}/org/sandboxes-v2/sandboxes/${sandboxId}/runs`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to run the action');
      return ((await res.json()) as { data: { id: string } }).data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RUNS_KEY(sandboxId) }),
  });
}
